// The routines list, shared by AUTOMATISATIONS and the agent drawer (SPEC-UX-V03 §B): one row per
// routine (title, badges, what it does and for whom, next run, last run linked to its task, runs),
// the active switch (manage), LANCER (operate), Modifier and Supprimer (manage, after confirmation),
// live `routine` / `routine.removed` events. The data comes from a routine source (GET /api/routines,
// narrowed with ?agentId= / ?floorId= / ?deptId=) that several lists may share — the agent drawer
// feeds its overview (the next three, compact) and its Routines tab from one source.
// The routine editor opened from a list is closed when the list is destroyed.
import { h, button, iconButton, switchToggle, badge, emptyState, spinner, classNames } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { withQuery } from '../../api.js';
import { deptColor, formatDateTime, relativeTime } from '../../format.js';
import { can as canIn } from '../../store.js';
import {
  toList, upsertById, removeById, groupByDept, groupByAgent, sortRoutines, routineNextAt, routineLastAt, routineLastIdleAt, routineLastBlocked, routineLastSkippedAt, routineRuns,
  officeIndex, createdTaskId, agentGroups,
} from './model.js';
import { openRoutineEditor } from './routineeditor.js';
import { childLayers } from './widgets.js';
import './strings.js';

/** Relative times ("il y a 5 min", next run) are refreshed every minute while a list is shown. */
const MINUTE_MS = 60_000;

/**
 * The autonomy badge of a routine or trigger: [label, tone].
 * @param {string} autonomy
 * @returns {[string, string]}
 */
export function autonomyBadge(autonomy) {
  if (autonomy === 'approve') return [t('auto.badge.approve'), 'amber'];
  if (autonomy === 'auto') return [t('auto.badge.auto'), 'dark'];
  return [t('auto.badge.inherit'), 'muted'];
}

/**
 * A live list of routines: loads GET /api/routines (with the scope as query, and filtered again here
 * in case the server ignores a filter), then follows `routine`, `routine.removed` and `hello`.
 * @param {object} ctx app context
 * @param {{agentId?: string, floorId?: string, deptId?: string}} [scope]
 * @returns {{readonly items: object[], readonly status: 'loading'|'ok'|'unavailable'|'error', readonly error: string,
 *   reload(): Promise<void>, upsert(routine: object): void, remove(id: string): void,
 *   subscribe(fn: () => void): () => void, destroy(): void}}
 */
export function createRoutineSource(ctx, { agentId = '', floorId = '', deptId = '' } = {}) {
  const subs = new Set();
  const offs = [];
  let items = [];
  let status = 'loading';
  let error = '';
  let seq = 0;
  let destroyed = false;
  const inScope = (r) => (!agentId || r.agentId === agentId) && (!floorId || r.floorId === floorId) && (!deptId || r.deptId === deptId);
  const has = (id) => items.some((r) => r.id === id);

  function notify() {
    if (destroyed) return;
    for (const fn of [...subs]) { try { fn(); } catch (err) { console.error('[routines]', err); } }
  }

  async function reload() {
    if (destroyed) return;
    const my = ++seq;
    status = 'loading';
    notify();
    try {
      const res = await ctx.api.get(withQuery('/api/routines', { agentId, floorId, deptId }));
      if (destroyed || my !== seq) return;
      items = toList(res, 'routines').filter(inScope);
      status = 'ok';
      error = '';
    } catch (err) {
      if (destroyed || my !== seq) return;
      status = err?.status === 404 ? 'unavailable' : 'error';
      error = err?.message || t('common.error');
    }
    notify();
  }

  function upsert(r) {
    if (!r?.id || destroyed) return;
    // A routine moved to another agent (or floor) leaves a scoped list.
    items = inScope(r) ? upsertById(items, r) : removeById(items, r.id);
    notify();
  }

  function remove(id) {
    if (!id || !has(id) || destroyed) return;
    items = removeById(items, id);
    notify();
  }

  offs.push(ctx.events.on('routine', (d) => {
    const r = d?.routine && typeof d.routine === 'object' ? d.routine : d;
    if (!r?.id || status !== 'ok') return;
    if (!inScope(r) && !has(r.id)) return;
    upsert(r);
  }));
  offs.push(ctx.events.on('routine.removed', (d) => remove(d?.id)));
  offs.push(ctx.events.on('hello', () => { reload(); }));
  reload();

  return {
    get items() { return items; },
    get status() { return status; },
    get error() { return error; },
    reload,
    upsert,
    remove,
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    destroy() {
      destroyed = true;
      seq += 1;
      subs.clear();
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
    },
  };
}

/**
 * Builds a routines list.
 * @param {object} ctx app context
 * @param {{agentId?: string, floorId?: string, deptId?: string, limit?: number, compact?: boolean,
 *   group?: 'dept'|'agent'|'none', source?: ReturnType<typeof createRoutineSource>,
 *   onChange?: (list: object) => void, emptyText?: string}} [opts]
 *   agentId / floorId / deptId: the fixed scope (the agent drawer passes agentId: new routines are
 *   created for this agent, its choice locked); limit: rows shown at most (0 = all); compact: short
 *   rows without the last run, the runs count and Supprimer (the overview); group: 'dept' (default),
 *   'agent' (« Par agent ») or 'none' ('none' by default with an agentId); source: a shared source
 *   (kept alive by its owner), else the list makes and destroys its own; onChange: called after each
 *   render (tab counts).
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void, create(defaults?: object): object|null,
 *   edit(routine: object): object|null, setFilter(patch: {floorId?: string, agentId?: string, query?: string,
 *   group?: string}): void, render(): void, canCreate(): boolean, readonly items: object[], readonly status: string}}
 */
export function createRoutineList(ctx, opts = {}) {
  const { agentId = '', floorId = '', deptId = '', limit = 0, compact = false } = opts;
  const ownSource = !opts.source;
  const source = opts.source || createRoutineSource(ctx, { agentId, floorId, deptId });
  const children = childLayers(null);
  const filter = { floorId: '', agentId: '', query: '', group: opts.group || (agentId ? 'none' : 'dept') };
  const el = h('div', { class: classNames(['auto-rlist', { 'is-compact': compact }]) });
  const can = (need, scope) => canIn(ctx.store.get(), need, scope);
  const now = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  const fk = (node, key) => { node.dataset.fk = key; return node; };
  const office = () => ctx.store.get().office || {};
  const fixedAgent = () => (agentId ? (office().agents || []).find((a) => a.id === agentId) || null : null);
  let destroyed = false;
  let api = null;

  /** True when the user may create a routine here (manage on the agent's department, or on any). */
  function canCreate() {
    if (agentId) {
      const a = fixedAgent();
      return !!a && can('manage', { floorId: a.floorId, deptId: a.deptId });
    }
    return agentGroups(ctx.store.get(), 'manage').length > 0;
  }

  function defaultAgentId() {
    if (agentId) return agentId;
    const s = ctx.store.get();
    const groups = agentGroups(s, 'manage');
    const onFloor = groups.find((g) => g.floorId === (filter.floorId || s.ui?.floorId)) || groups[0];
    return (filter.agentId && groups.some((g) => g.agents.some((a) => a.id === filter.agentId)) ? filter.agentId : '')
      || onFloor?.agents[0]?.id || '';
  }

  function openEditor(r, defaults = {}) {
    if (destroyed) return null;
    return children.add(openRoutineEditor(ctx, {
      routine: r,
      defaults: r ? {} : { agentId: defaultAgentId(), ...defaults },
      lockAgent: !!agentId,
      onSaved: (saved) => { if (saved?.id) source.upsert(saved); },
      onDeleted: (id) => source.remove(id),
    }));
  }

  // --- actions ---------------------------------------------------------------------------------------
  async function setPaused(r, paused, toggle) {
    toggle.input.disabled = true;
    try {
      const res = await ctx.api.patch(`/api/routines/${encodeURIComponent(r.id)}`, { paused });
      const saved = res?.routine && typeof res.routine === 'object' ? res.routine : res;
      source.upsert(saved?.id ? saved : { ...r, paused });
      ctx.toast.success(paused ? t('auto.routines.pausedToast') : t('auto.routines.resumedToast'));
    } catch (err) {
      toggle.input.checked = !r.paused;
      ctx.toast.error(err);
    } finally {
      toggle.input.disabled = false;
    }
  }

  async function runNow(r, btn) {
    btn.disabled = true;
    btn.classList.add('is-busy');
    try {
      const res = await ctx.api.post(`/api/routines/${encodeURIComponent(r.id)}/run`, {});
      if (res?.id && res.state) ctx.store.applyTask?.(res);
      const taskId = createdTaskId(res);
      ctx.toast.success(t('auto.routines.ran'), taskId
        ? { action: { label: t('auto.routines.openTask'), onClick: () => ctx.navigate(`#/task/${encodeURIComponent(taskId)}`) } }
        : {});
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-busy');
    }
  }

  async function deleteRoutine(r) {
    const ok = await ctx.overlay.confirm({
      title: t('auto.routines.deleteTitle'), message: t('auto.routines.deleteText', { title: r.title || '' }),
      confirmLabel: t('auto.routines.delete'), danger: true,
    });
    if (!ok || destroyed) return;
    try {
      await ctx.api.del(`/api/routines/${encodeURIComponent(r.id)}`);
      source.remove(r.id);
      ctx.toast.success(t('auto.routines.deleted'));
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  // --- rendering -------------------------------------------------------------------------------------
  function metaItem(label, value) {
    return h('div', { class: 'auto-meta-item' }, h('span', { class: 'mono-label' }, label), h('span', { class: 'auto-meta-value' }, value));
  }

  function routineRow(r, idx) {
    const agent = idx.agents.get(r.agentId);
    const scope = { floorId: r.floorId, deptId: r.deptId };
    const canManage = can('manage', scope);
    const canOperate = can('operate', scope);
    const next = r.paused ? null : routineNextAt(r);
    const last = routineLastAt(r);
    const [autLabel, autTone] = autonomyBadge(r.autonomy);

    let toggle = null;
    if (canManage) {
      toggle = switchToggle(t('auto.routines.activeLabel'), { checked: !r.paused, onChange: (v) => setPaused(r, !v, toggle) });
      toggle.input.setAttribute('aria-label', t('auto.routines.toggleAria', { title: r.title || '' }));
      fk(toggle.input, `r:${r.id}:toggle`);
      toggle.classList.add('auto-switch');
    }
    // The last pass found nothing to do: its task is gone (deleted after 10 min), say so instead.
    const idleAt = routineLastIdleAt(r);
    // The last pass was blocked by an external cause (« BLOQUÉ : … »): say why, link to its task.
    const blocked = idleAt ? null : routineLastBlocked(r);
    const blockedReason = blocked ? blocked.reason || t('auto.routines.lastBlockedNoReason') : '';
    const lastValue = idleAt
      ? h('span', { class: 'auto-idle', title: t('auto.routines.lastIdleTitle', { when: relativeTime(idleAt, now()) }) },
        t('auto.routines.lastIdle', { when: relativeTime(idleAt, now()) }))
      : blocked
      ? (r.state?.lastTaskId
        ? fk(h('button', {
          type: 'button', class: 'link-btn auto-blocked', title: t('auto.routines.lastBlockedTitle', { reason: blockedReason, when: relativeTime(blocked.at, now()) }),
          'aria-label': t('auto.routines.lastTaskAria', { title: r.title || '' }),
          onClick: () => ctx.navigate(`#/task/${encodeURIComponent(r.state.lastTaskId)}`),
        }, t('auto.routines.lastBlocked', { reason: blockedReason })), `r:${r.id}:last`)
        : h('span', { class: 'auto-blocked', title: t('auto.routines.lastBlockedTitle', { reason: blockedReason, when: relativeTime(blocked.at, now()) }) },
          t('auto.routines.lastBlocked', { reason: blockedReason })))
      : last
      ? (r.state?.lastTaskId
        ? fk(h('button', {
          type: 'button', class: 'link-btn', 'aria-label': t('auto.routines.lastTaskAria', { title: r.title || '' }),
          onClick: () => ctx.navigate(`#/task/${encodeURIComponent(r.state.lastTaskId)}`),
        }, relativeTime(last, now())), `r:${r.id}:last`)
        : relativeTime(last, now()))
      : t('auto.never');
    // The last scheduled pass was skipped (its previous task was still unfinished): a discreet note.
    const skippedAt = compact ? null : routineLastSkippedAt(r);

    const actions = [];
    if (canOperate) {
      const runBtn = button(t('auto.routines.run'), { variant: 'secondary', size: 'sm', icon: 'bolt', title: t('auto.routines.runTitle'), onClick: () => runNow(r, runBtn) });
      actions.push(fk(runBtn, `r:${r.id}:run`));
    }
    if (canManage) {
      actions.push(fk(button(t('auto.routines.edit'), { variant: 'ghost', size: 'sm', onClick: () => openEditor(r) }), `r:${r.id}:edit`));
      if (!compact) actions.push(fk(iconButton('close', `${t('auto.routines.delete')} — ${r.title || ''}`, { variant: 'ghost', size: 16, class: 'auto-del', onClick: () => deleteRoutine(r) }), `r:${r.id}:del`));
    }
    // In an agent's list the agent goes without saying.
    const who = agentId ? '' : agent ? `${agent.name}${agent.role ? ` — ${agent.role}` : ''}` : t('auto.unknownAgent');
    const nextValue = r.paused ? t('auto.routines.paused') : next ? formatDateTime(next, now()) : t('auto.none');

    return h('li', {
      class: ['auto-item', { 'is-paused': !!r.paused, 'is-compact': compact }], dataset: { routine: r.id },
      style: { '--dept': deptColor(idx.depts.get(r.deptId)?.color, r.deptId) },
    },
    h('div', { class: 'auto-item-main' },
      h('div', { class: 'auto-item-head' },
        h('h4', { class: 'auto-item-title' }, r.title || '—'),
        h('div', { class: 'auto-badges' },
          r.paused ? badge(t('auto.routines.paused'), 'muted') : null,
          badge(autLabel, autTone),
          r.outputFormat === 'json' ? badge(t('auto.badge.json'), 'info') : null,
          r.state?.lastLate ? badge(t('auto.routines.late'), 'amber') : null,
          r.state?.lastError ? badge(t('common.error'), 'danger', { title: String(r.state.lastError) }) : null)),
      h('p', { class: 'auto-item-desc' }, [r.description, who].filter(Boolean).join(' · ')),
      h('div', { class: 'auto-meta' },
        metaItem(t('auto.routines.next'), nextValue),
        compact ? null : metaItem(t('auto.routines.last'), lastValue),
        compact ? null : metaItem(t('auto.routines.runs'), String(routineRuns(r)))),
      skippedAt ? h('p', { class: 'auto-skipped', title: t('auto.routines.skippedTitle') },
        t('auto.routines.skipped', { when: relativeTime(skippedAt, now()) })) : null),
    h('div', { class: 'auto-item-side' }, toggle, h('div', { class: 'auto-actions' }, actions)));
  }

  function groupHeader(g) {
    if (filter.group === 'agent') {
      const name = g.agentName || t('auto.unknownAgent');
      return h('div', { class: 'auto-group-head' },
        h('span', { class: 'auto-dot', style: { '--dept': deptColor(g.color, g.deptId) }, 'aria-hidden': 'true' }),
        h('h3', { class: 'auto-group-title' }, g.agentName
          ? fk(h('button', {
            type: 'button', class: 'link-btn auto-agent-link', 'aria-label': t('auto.group.agentLink', { name }),
            onClick: () => ctx.navigate(`#/agent/${encodeURIComponent(g.agentId)}/routines`),
          }, name), `g:${g.agentId}`)
          : name),
        h('span', { class: 'auto-group-sub muted' }, [g.floorName, g.deptName].filter(Boolean).join(' · ')),
        h('span', { class: 'auto-group-count mono-label' }, String(g.items.length)));
    }
    return h('div', { class: 'auto-group-head' },
      h('span', { class: 'auto-dot', style: { '--dept': deptColor(g.color, g.deptId) }, 'aria-hidden': 'true' }),
      h('h3', { class: 'auto-group-title' }, [g.floorName, g.deptName].filter(Boolean).join(' · ') || t('auto.none')),
      h('span', { class: 'auto-group-count mono-label' }, String(g.items.length)));
  }

  function stateBlock() {
    const { status } = source;
    if (status === 'loading') return h('div', { class: 'auto-state' }, spinner(t('auto.loading')), h('span', { class: 'muted' }, t('auto.loading')));
    if (status === 'unavailable') return emptyState({ icon: 'info', title: t('common.soon'), text: t('auto.unavailable') });
    if (status === 'error') {
      return emptyState({
        icon: 'alert', title: t('common.error'), text: source.error,
        action: button(t('auto.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => source.reload() }),
      });
    }
    return null;
  }

  function emptyBlock() {
    const creatable = canCreate();
    const a = fixedAgent();
    if (compact) return h('p', { class: 'auto-rlist-empty muted' }, a ? t('auto.routines.emptyAgent', { name: a.name }) : t('auto.routines.emptyTitle'));
    return emptyState({
      icon: 'repeat',
      title: a ? t('auto.routines.emptyAgent', { name: a.name }) : t('auto.routines.emptyTitle'),
      text: opts.emptyText || (creatable ? t('auto.routines.emptyText') : t('auto.routines.emptyReadOnly')),
      action: creatable ? fk(button(t('auto.routines.create'), { variant: 'primary', size: 'sm', icon: 'plus', onClick: () => openEditor(null) }), 'empty:create') : null,
    });
  }

  function content() {
    const block = stateBlock();
    if (block) return block;
    if (!source.items.length) return emptyBlock();
    const idx = officeIndex(office());
    const sorted = sortRoutines(source.items);
    const groupOpts = { floorId: filter.floorId, agentId: filter.agentId, query: filter.query, text: (r) => [r.title, r.prompt, r.description] };
    const groups = filter.group === 'agent' ? groupByAgent(sorted, office(), groupOpts) : groupByDept(sorted, office(), groupOpts);
    if (!groups.length) return emptyState({ icon: 'search', title: t('auto.noResults') });
    if (filter.group === 'none') {
      // One list (the agent's routines keep their next-run order across departments).
      const shown = sorted.filter((r) => groups.some((g) => g.items.includes(r)));
      const rows = limit > 0 ? shown.slice(0, limit) : shown;
      return h('ul', { class: 'auto-list', 'aria-label': t('automations.tabs.routines') }, rows.map((r) => routineRow(r, idx)));
    }
    return h('div', { class: 'auto-groups' }, groups.map((g) => {
      const label = filter.group === 'agent' ? (g.agentName || t('auto.unknownAgent')) : [g.floorName, g.deptName].filter(Boolean).join(' · ');
      return h('section', { class: 'auto-group', 'aria-label': label },
        groupHeader(g), h('ul', { class: 'auto-list' }, g.items.map((r) => routineRow(r, idx))));
    }));
  }

  function render() {
    if (destroyed) return;
    const active = globalThis.document?.activeElement;
    const focusKey = active && el.contains?.(active) !== false ? active?.dataset?.fk || null : null;
    el.replaceChildren(content());
    if (focusKey) {
      const again = [...el.querySelectorAll('[data-fk]')].find((n) => n.dataset.fk === focusKey);
      again?.focus?.();
    }
    try { opts.onChange?.(api); } catch (err) { console.error('[routines]', err); }
  }

  const offs = [source.subscribe(render)];
  if (ctx.store.on) offs.push(ctx.store.on('office', render));
  let minuteTimer = setInterval(render, MINUTE_MS);

  api = {
    el,
    reload: () => source.reload(),
    render,
    canCreate,
    /** Opens the editor for a new routine (the agent locked in an agent's list). */
    create: (defaults = {}) => (canCreate() ? openEditor(null, defaults) : null),
    edit: (r) => openEditor(r),
    setFilter(patch = {}) {
      let changed = false;
      for (const k of ['floorId', 'agentId', 'query', 'group']) {
        if (typeof patch[k] === 'string' && patch[k] !== filter[k]) { filter[k] = patch[k]; changed = true; }
      }
      if (changed) render();
    },
    get items() { return source.items; },
    get status() { return source.status; },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      clearInterval(minuteTimer);
      minuteTimer = null;
      for (const off of offs.splice(0)) { try { off?.(); } catch { /* ignore */ } }
      children.closeAll('route');
      if (ownSource) source.destroy();
    },
  };
  render();
  return api;
}
